import React, { useState, useEffect } from 'react';
import { settingsAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, X, Save, Loader2, Settings, Tag, List } from 'lucide-react';

function EditableList({ title, icon: Icon, items, onChange, color = 'sky' }) {
  const [newItem, setNewItem] = useState('');
  const addItem = () => {
    const val = newItem.trim();
    if (!val || items.includes(val)) return;
    onChange([...items, val]);
    setNewItem('');
  };
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
          <Icon size={16} className={`text-${color}-500`} />
          {title}
          <Badge variant="secondary" className="ml-auto text-[10px]">{items.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-1 bg-slate-100 rounded-full px-3 py-1 text-xs text-slate-700">
              <span>{item.replace(/_/g, ' ')}</span>
              <button onClick={() => removeItem(i)} className="text-slate-400 hover:text-red-500 ml-0.5"><X size={12} /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={newItem} onChange={e => setNewItem(e.target.value)} placeholder="Add new..."
            className="h-8 text-xs" onKeyDown={e => e.key === 'Enter' && addItem()} />
          <Button variant="outline" size="sm" onClick={addItem} className="h-8 px-3 gap-1"><Plus size={12} />Add</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EditableVariants({ variants, onChange }) {
  const [newCategory, setNewCategory] = useState('');
  const [newVariant, setNewVariant] = useState('');
  const [activeCategory, setActiveCategory] = useState('');

  const addCategory = () => {
    const val = newCategory.trim();
    if (!val || variants[val]) return;
    onChange({ ...variants, [val]: [] });
    setNewCategory('');
    setActiveCategory(val);
  };

  const addVariant = (cat) => {
    const val = newVariant.trim();
    if (!val || (variants[cat] || []).includes(val)) return;
    onChange({ ...variants, [cat]: [...(variants[cat] || []), val] });
    setNewVariant('');
  };

  const removeVariant = (cat, idx) => {
    onChange({ ...variants, [cat]: variants[cat].filter((_, i) => i !== idx) });
  };

  const removeCategory = (cat) => {
    const v = { ...variants };
    delete v[cat];
    onChange(v);
    if (activeCategory === cat) setActiveCategory('');
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
          <List size={16} className="text-violet-500" />
          Product Variants by Category
          <Badge variant="secondary" className="ml-auto text-[10px]">{Object.keys(variants).length} categories</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-1.5">
          {Object.keys(variants).map(cat => (
            <button key={cat} onClick={() => setActiveCategory(activeCategory === cat ? '' : cat)}
              className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs transition-colors ${activeCategory === cat ? 'bg-violet-100 text-violet-700 ring-1 ring-violet-300' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
              {cat} ({(variants[cat] || []).length})
              <span onClick={(e) => { e.stopPropagation(); removeCategory(cat); }} className="text-slate-400 hover:text-red-500 ml-0.5"><X size={11} /></span>
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={newCategory} onChange={e => setNewCategory(e.target.value)} placeholder="New category..."
            className="h-8 text-xs" onKeyDown={e => e.key === 'Enter' && addCategory()} />
          <Button variant="outline" size="sm" onClick={addCategory} className="h-8 px-3 gap-1 shrink-0"><Plus size={12} />Add Category</Button>
        </div>

        {activeCategory && variants[activeCategory] && (
          <div className="border border-violet-200 rounded-lg p-3 bg-violet-50/50 space-y-2">
            <Label className="text-xs font-semibold text-violet-700">Variants for: {activeCategory}</Label>
            <div className="flex flex-wrap gap-1.5">
              {variants[activeCategory].map((v, i) => (
                <div key={i} className="flex items-center gap-1 bg-white rounded-full px-2.5 py-0.5 text-xs text-slate-700 border border-slate-200">
                  {v}
                  <button onClick={() => removeVariant(activeCategory, i)} className="text-slate-400 hover:text-red-500"><X size={11} /></button>
                </div>
              ))}
              {variants[activeCategory].length === 0 && <span className="text-xs text-slate-400">No variants yet</span>}
            </div>
            <div className="flex gap-2">
              <Input value={newVariant} onChange={e => setNewVariant(e.target.value)} placeholder="Add variant..."
                className="h-7 text-xs" onKeyDown={e => { if (e.key === 'Enter') addVariant(activeCategory); }} />
              <Button variant="outline" size="sm" onClick={() => addVariant(activeCategory)} className="h-7 px-2 gap-1 text-xs"><Plus size={11} /></Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PlatformSettingsPage() {
  const { user } = useAuth();
  const isAdmin = isKRRole(user);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    settingsAPI.getCategories().then(r => setData(r.data)).catch(() => toast.error('Failed to load settings')).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await settingsAPI.updateCategories(data);
      setData(res.data);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    }
    setSaving(false);
  };

  if (!isAdmin) return <div className="flex items-center justify-center h-[60vh]"><p className="text-slate-500">Admin access required.</p></div>;
  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="animate-spin text-slate-400" size={28} /></div>;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="platform-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Administration</p>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Platform Settings</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage categories, options, and configurations used across the platform</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-1.5" data-testid="save-settings-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save All Changes
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EditableList title="Product Categories" icon={Tag} color="sky"
          items={data.product_categories || []}
          onChange={v => setData(d => ({ ...d, product_categories: v }))} />

        <EditableList title="Contract Statuses" icon={Settings} color="red"
          items={data.sale_statuses || []}
          onChange={v => setData(d => ({ ...d, sale_statuses: v }))} />
      </div>

      <EditableVariants
        variants={data.product_variants || {}}
        onChange={v => setData(d => ({ ...d, product_variants: v }))} />
    </div>
  );
}
